import { useEffect, useRef } from "react";
import QRCode from "qrcode";

/**
 * Tiny canvas-based QR renderer. We tile the entire dataURL approach
 * because <canvas> renders crisp on retina without us doing any
 * device-pixel-ratio math, and `qrcode` writes straight to it.
 *
 * `value` is what the QR encodes — typically the redeem deeplink
 * `https://beta.gntl.app/r/<code>` so phone-camera scanners route
 * straight there.
 */
export function QrCode({
  value,
  size = 160,
  centerEmoji,
}: {
  value: string;
  size?: number;
  /** Optional emoji to overlay in the center. Requires the higher-EC
   *  rendering below so the QR still scans with the modules obscured. */
  centerEmoji?: string;
}) {
  const canvas = useRef<HTMLCanvasElement | null>(null);
  useEffect(() => {
    if (!canvas.current) return;
    const el = canvas.current;
    QRCode.toCanvas(el, value, {
      width: size,
      margin: 1,
      // 'H' = 30% error correction; safe to obscure ~15-20% of area.
      errorCorrectionLevel: centerEmoji ? "H" : "M",
      color: {
        dark: "#1a1a2e",
        light: "#ffffff",
      },
    })
      .then(() => {
        // Paint the white tile + emoji onto the canvas directly so
        // browser-level dark-mode inverters (Dark Reader etc.) can't
        // flip its color — they don't touch canvas pixel data.
        if (!centerEmoji) return;
        const ctx = el.getContext("2d");
        if (!ctx) return;
        const tile = size * 0.2;
        const x = (size - tile) / 2;
        const y = (size - tile) / 2;
        ctx.fillStyle = "#ffffff";
        ctx.fillRect(x, y, tile, tile);
        ctx.font = `${size * 0.16}px "Apple Color Emoji", "Segoe UI Emoji", "Noto Color Emoji", sans-serif`;
        ctx.textAlign = "center";
        ctx.textBaseline = "middle";
        // Nudge down — emoji glyphs sit a touch high inside their em box.
        ctx.fillText(centerEmoji, size / 2, size / 2 + size * 0.015);
      })
      .catch(() => {
        // qrcode throws on overlong input; nothing to do — render blank.
      });
  }, [value, size, centerEmoji]);
  return (
    <canvas
      ref={canvas}
      width={size}
      height={size}
      className="rounded-md"
      aria-label="Scan to open the redeem link on your phone"
    />
  );
}
